import React from 'react';
import Typography from '@mui/material/Typography';
import { Categories } from '@pizza-hut-us-development/client-core';
import useStyles from '../../styles';
import { useCategoryDisplayNameContext } from '../context/CategoryDisplayNameProvider';
import CategoryHeader from '../../../common/components/CategoryHeader';
import CaloriesDisclaimer from '@/common/CaloriesDisclaimer/CaloriesDisclaimer';
import { ItemAnalytics } from '@/dataAnalytics/analyticsTypes';
import LocalizedMenuSkeleton from '@/menu/deals/LocalizedMenu/LocalizedMenuSkeleton';
import LegalDisclaimer from '@/common/LegalDisclaimer/LegalDisclaimer';
import ProductTilesList from './ProductTilesList';
import {
  SodiumWarningDisclaimer
} from '@/clientCore/components/SodiumWarningDisclaimer/components/SodiumWarningDisclaimer';
import useSodiumAlertForStore from '@/clientCore/components/SodiumWarningDisclaimer/hooks/useSodiumAlertForStore';
import { useGetMenuPageData } from '@/menu/hooks/useGetMenuPageData';
import { useMenuPageAnalytics } from '@/menu/hooks/useMenuPageAnalytics';

interface LocalizedMenuPageProps {
  onPageLoad?: (items: ItemAnalytics[]) => void;
}

const LocalizedMenuPage = ({ onPageLoad }: LocalizedMenuPageProps): JSX.Element => {
  const classes = useStyles();
  const { isShowSodiumAlertForStore } = useSodiumAlertForStore();

  const {
    categoryDisplayName,
    heading,
    subHeading,
    categoryID,
    loadingCategoryName
  } = useCategoryDisplayNameContext();

  const {
    products,
    subCategories,
    loading
  } = useGetMenuPageData(categoryID);

  const isLineup = categoryID === Categories.LINEUP;
  const noProductsError = `This restaurant doesn't have ${categoryDisplayName}`;

  useMenuPageAnalytics(categoryDisplayName, loadingCategoryName, subCategories, products, onPageLoad);

  if (loading) {
    return <LocalizedMenuSkeleton />;
  }

  return (
    <>
      <main
        data-testid={`${categoryID}-localized-menu-page`}
        className={classes.localizedMenuPageMain}
      >
        <section>
          <CategoryHeader
            loadingCategoryName={loadingCategoryName}
            pageDisplayName={categoryDisplayName}
            handleCTAClick={() => null}
            headingText={heading}
            subHeadingText={subHeading}
            isLocalized
            customTestId={`${categoryID}-localized-menu-page-header`}
          />
          <div
            className={classes.tilesGrid}
            data-testid={`${categoryID}-localized-menu-page-content`}
          >
            {!products.length && (
              <Typography>{noProductsError}</Typography>
            )}
            <ProductTilesList products={products} />
          </div>
        </section>
        {subCategories?.map((subCategory, index) => (
          <section
            key={`subcategory-${index}`}
            data-testid={`subcategory-section-${index}`}
          >
            <Typography variant="h2" className={classes.subcategoryHeading}>
              {subCategory.heading}
            </Typography>
            <div className={classes.tilesGrid}>
              <ProductTilesList products={subCategory.products} />
            </div>
          </section>
        ))}
      </main>
      {isLineup && <LegalDisclaimer />}
      <CaloriesDisclaimer />
      {isShowSodiumAlertForStore && <SodiumWarningDisclaimer />}
    </>
  );
};

export default LocalizedMenuPage;
